<script>
import { forEach } from 'lodash';

import Constants from '../constants';

import commonFooter from '@/components/commonFooter';
import commonNav from '@/components/commonNav';
import movePageOnSwipe from '@/components/movePageOnSwipe';
import shareBtn from '@/components/shareBtn';
import hoverDesign from '@/components/hoverDesign';
import pageTitle from '@/components/pageTitle';
import demoCdList from '@/components/demoCdList';

import setMetaUtil from '@/util/setMetaUtil';
import {imagesLoad} from '@/util/assetLoadUtil';

import img15 from '@/assets/img/discography/15/m.jpg';
import img14 from '@/assets/img/discography/14/m.jpg';
import img13 from '@/assets/img/discography/13/m.jpg';
import img12 from '@/assets/img/discography/12/m.jpg';
import img11 from '@/assets/img/discography/11/m.jpg';
import img10 from '@/assets/img/discography/10/m.jpg';

export default {
  name: Constants.ROUTER.DISCOGRAPHY,
  components: {
    commonFooter,
    commonNav,
    movePageOnSwipe,
    shareBtn,
    hoverDesign,
    pageTitle,
    demoCdList,
  },
  data() {
    return {
      ready: false,
      cdList: [],
      windowHeight: Constants.WINDOW.MIN_HEIGHT.viewSm,
      leftMoveto: { name: Constants.ROUTER.BIOGRAPHY },
      rightMoveto: { name: Constants.ROUTER.INDEX, query: { isInformation: 1, } },
    };
  },
  async created() {
    setMetaUtil.setMeta(this, document,
      `${this.$i18n.messages.ja.message.discography.pageName} - ${this.$i18n.messages.ja.message.bandName}`,
      this.$i18n.messages.ja.message.discography.description,
    );
    document.dispatchEvent(new Event('custom-render-trigger'));

    this.cdList = [];
    forEach(this.$t('message.discography.albums'), (v) => {
      this.cdList.unshift(v);
    });    

    window.onscroll = () => { this.onscroll(); }
    window.onresize = () => { this.onscroll(); }
    this.onscroll();

    await imagesLoad([
      img15,
      img14,
      img13,
      img12,
      img11,
      img10,
    ]);

    setTimeout(()=> {
      this.ready = true;
    }, 1);
  },
  methods: {
    moveToAlbumPage(id) {
      this.$router.push({ name: Constants.ROUTER[`ALBUM${id}`], });
    },
    moveToDemo() {
      this.$router.push({name: Constants.ROUTER.DEMO});
    },
    onscroll() {
      const minHeight = Constants.WINDOW.MIN_HEIGHT[this.$mq];
      this.windowHeight = window.innerHeight < minHeight ? minHeight : window.innerHeight;
    },
  },
};
</script>

<template>
  <move-page-on-swipe
    :left-move-to="leftMoveto"
    :right-move-to="rightMoveto"
    class="view-discography page"
  >
    <common-nav/>
    <div id="contents-main" :style="{'min-height': windowHeight + 'px'}" :class="[ready ? 'is-ready' : '', $mq]">

      <page-title :title-text="$t('message.discography.pageName')" title-type="moon" />

      <div class="cd-list">
        <div class="cd-item"
          v-for="cdItem in cdList" :key="cdItem.id"
          :title="cdItem.name"
          :class="`is-${cdItem.id}`"
          @click="moveToAlbumPage(cdItem.id)"
        >
          <div class="jacket"></div>
          <div class="text-container">
            <p>
              <span :data-text="cdItem.name">{{cdItem.name}}</span>
            </p>
            <p>
              <span class="date" :data-text="cdItem.date">{{cdItem.date}}</span>
            </p>
          </div>
        </div>
      </div>

      <!-- demo -->
      <div class="demo-cd" @click="moveToDemo(0)" :title="$t('message.demo.pageName')">
        <page-title :title-text="$t('message.demo.pageName')" title-type="libra" />
        <hover-design hover-color="white"/>
      </div>
      <demo-cd-list />
    </div>

    <common-footer/>
    <share-btn :is-fixed="true"/>
  </move-page-on-swipe>
</template>

<style lang="scss" scoped>
  @import "../css/variables";
  .page {
    position: relative;
    display: flex;
    flex-direction:column;

    #contents-main {
      flex-direction: column;
      justify-content:center;
      padding-top: $commonNavHeight * 2;
      width: 100%;
      margin: 0 auto;
      position: relative;
      opacity: 0;
      &.is-ready {
        opacity: 1;
      }
      
      .cd-list {
        $gridSize: 250px;
        $gridGap: 30px;
        grid-gap: $gridGap;
        display: grid;
        grid-auto-rows: $gridSize;
        width: 100%;
        margin: 50px 0;
        .cd-item {
          transition: 0.2s ease;
          position: relative;
          cursor: pointer;

          .jacket {
            @include absolute100;
            max-width: 100%;
            height: 100%;
            aspect-ratio: 1 / 1;
            background-repeat: no-repeat;
            background-position: 50% 50%;
            background-size: $gridSize $gridSize;
            filter: grayscale(0%);
            transition: 0.3s ease;
          }
          &.is-10 .jacket { background-image: url(../assets/img/discography/10/m.jpg); }
          &.is-11 .jacket { background-image: url(../assets/img/discography/11/m.jpg); }
          &.is-12 .jacket { background-image: url(../assets/img/discography/12/m.jpg); }
          &.is-13 .jacket { background-image: url(../assets/img/discography/13/m.jpg); }
          &.is-14 .jacket { background-image: url(../assets/img/discography/14/m.jpg); }
          &.is-15 .jacket { background-image: url(../assets/img/discography/15/m.jpg); }          
          .text-container {
            position: absolute;
            bottom: 0;
            width: 100%;
            padding: 30px 5px 0 0;
            background: $bgColorA50;
            text-align: right;
            span {
              position: relative;
              max-width: 100%;
              display: inline-block;
              @include ellipsis;
              &::after {
                content: attr(data-text)"";
                position: absolute;
                top: 0;
                left: 0;
                width: 0%;
                white-space: nowrap;
                overflow: hidden;
                color: $accentColor2;
                filter: drop-shadow(0px 0px 3px $accentColor2);
              }
            }
          }
          &:nth-of-type(1) {
            grid-column: 1 / 3;
            grid-row: 1 / span 2;
            .jacket {
              background-size: $gridSize*2+$gridGap $gridSize*2+$gridGap;
            }
          }

          &:hover {
            .jacket {
              filter: grayscale(100%);
              background-size: $gridSize*1.1 $gridSize*1.1;
            }
            &:nth-of-type(1) {
              .jacket {
                background-size: ($gridSize*2+$gridGap)*1.1 ($gridSize*2+$gridGap)*1.1;
              }
            }
            .text-container {
              span {
                color: $bgColor;
                &::after {
                  animation: nameAfterAnim 0.3s ease 0s 1;
                  animation-fill-mode: forwards;
                }
              }
            }
          }

        }
      }

      .demo-cd {
        position: relative;
        margin-bottom: 30px;
        &:hover {
          color: $accentColor2;
        }
      }
    }
    // responsible -----------------------------------------------
    &.viewLg {
      #contents-main {
        .cd-list {
          grid-template-columns: 1fr 1fr 1fr 1fr;
          .cd-item {
            .text-container {
              font-size: 1.1em;
            }
          }
        }
        .demo-cd {width: 95%;}
      }
    }
    &.viewMd {
      #contents-main {
        .cd-list {
          grid-template-columns: 1fr 1fr;
          .cd-item {
          }
        }
        .demo-cd {width: 95%;}
      }
    }
    &.viewSm {
      #contents-main {
        .cd-list {
          grid-template-columns: 1fr 1fr;
          .cd-item {
          }
        }
        .demo-cd {width: 100%;}
      }
    }
  }

  @keyframes nameAfterAnim {
    0%{width: 0%;}
    99%{width: 100%;}
    100%{width: 100%; text-overflow:ellipsis;}
  }
</style>
